'use client';
import useSWR from "swr";
import { useEffect, useMemo, useState } from "react";
import { useInitialSettings, useSettingsState } from "../context/settings";
import useAllWithdrawalBalances from "./useAllWithdrawalBalances";
import { resolveExchangesURLForSelectedToken, resolveNetworkRoutesURL } from "../helpers/routes";
import LayerSwapApiClient from "../lib/apiClients/layerSwapApiClient";
import useExchangeNetworks from "./useExchangeNetworks";
import { useRecentNetworksStore } from "../stores/recentRoutesStore";
import { useRouteTokenSwitchStore } from "../stores/routeTokenSwitchStore";
import { getTotalBalanceInUSD } from "../helpers/balanceHelper";
import { SortingOption, useRouteSortingStore } from "../stores/routeSortingStore";
import { extractTokenElementsAsSuggested, sortSuggestedTokenElements } from "../helpers/routeUtils";
import { isExtendedSourceNetwork, mergeExtendedSourceRoutes } from "../lib/extendedRoutes/registry";
export default function useFormRoutes({ direction, values }, search, suggestionsLimit = 4) {
    const { routes, isLoading: routesLoading } = useRoutes({ direction, values });
    const { exchangesRoutes, isLoading: exchangesRoutesLoading } = useExchangeRoutes({ direction, values });
    const { networks: withdrawalNetworks, isLoading: exchangeSourceNetworksLoading } = useExchangeNetworks({
        fromExchange: values.fromExchange?.name,
        to: values.to?.name,
        toAsset: values.toAsset?.symbol
    });
    const { lockFrom, from, lockTo, to, lockFromAsset, fromAsset, lockToAsset, toAsset } = useInitialSettings();
    const groupByToken = useRouteTokenSwitchStore((s) => s.showTokens);
    const sortingOption = useRouteSortingStore((s) => s.sortingOption);
    const { balances, partialPublished, isLoading } = useAllWithdrawalBalances();
    const routesHistory = useRecentNetworksStore(state => state.recentRoutes);
    const loadingSuggestions = useMemo(() => {
        return !partialPublished && isLoading && direction === "from";
    }, [isLoading, direction, partialPublished]);
    // Apply query-based filtering
    const filteredRoutes = useMemo(() => {
        let filtered = filterRoutesByQuery(routes, direction, { lockFrom, from, lockTo, to, lockFromAsset, fromAsset, lockToAsset, toAsset });
        // In exchange flow (deposit_address), only show source networks that support deposit_address
        if (direction === 'from' && values.depositMethod === 'deposit_address') {
            filtered = filtered.filter(r => r.deposit_methods?.includes('deposit_address'));
        }
        return filtered;
    }, [routes, direction, lockFrom, from, lockTo, to, lockFromAsset, fromAsset, lockToAsset, toAsset, values.depositMethod]);
    const routeElements = useMemo(() => groupRoutes({
        routes: filteredRoutes,
        direction,
        balances,
        groupBy: groupByToken ? "token" : "network",
        recents: routesHistory,
        balancesLoaded: loadingSuggestions,
        search,
        suggestionsLimit,
        sortingOption
    }), [filteredRoutes, balances, direction, search, groupByToken, routesHistory, loadingSuggestions, suggestionsLimit, sortingOption]);
    const exchanges = useMemo(() => {
        return groupExchanges(exchangesRoutes, search, direction, { lockFrom, from, lockTo, to });
    }, [exchangesRoutes, search, direction, lockFrom, from, lockTo, to]);
    const selectedRoute = useMemo(() => resolveSelectedRoute(values, direction), [values, direction]);
    const selectedToken = useMemo(() => resolveSelectedToken(values, direction), [values, direction]);
    return useMemo(() => ({
        allRoutes: filteredRoutes,
        isLoading: routesLoading,
        routeElements,
        exchanges,
        exchangesRoutesLoading,
        exchangeNetworks: withdrawalNetworks,
        exchangeSourceNetworksLoading,
        selectedRoute,
        selectedToken,
    }), [
        filteredRoutes,
        routesLoading,
        routeElements,
        exchanges,
        exchangesRoutesLoading,
        withdrawalNetworks,
        exchangeSourceNetworksLoading,
        selectedRoute,
        selectedToken
    ]);
}
function useRoutesData(url, defaultData, fetcher) {
    const { data, isLoading } = useSWR(url, fetcher, {
        keepPreviousData: true,
        dedupingInterval: 10000,
    });
    const [routes, setRoutes] = useState(defaultData);
    useEffect(() => {
        if (!isLoading && data?.data)
            setRoutes(data.data);
    }, [isLoading, data]);
    return { routes, isLoading };
}
function filterRoutesByQuery(routes, direction, queryParams) {
    const { lockFrom, from, lockTo, to, lockFromAsset, fromAsset, lockToAsset, toAsset } = queryParams;
    const hasNetworkLock = direction === 'from' ? !!lockFrom : !!lockTo;
    const hasAssetLock = direction === 'from' ? !!lockFromAsset : !!lockToAsset;
    if (!hasNetworkLock && !hasAssetLock)
        return routes;
    // Resolve locked network (case-insensitive) and asset symbol (case-sensitive as before)
    const lockedNetworkName = direction === 'from'
        ? (lockFrom && from ? normalize(from) : undefined)
        : (lockTo && to ? normalize(to) : undefined);
    const lockedAssetSymbol = direction === 'from'
        ? (lockFromAsset ? fromAsset : undefined)
        : (lockToAsset ? toAsset : undefined);
    if (lockedNetworkName) {
        const filteredRoutes = routes.filter(r => normalize(r.name) === lockedNetworkName);
        if (lockedAssetSymbol) {
            return filteredRoutes
                .map(route => {
                const filteredTokens = route.tokens?.filter(t => t.symbol === lockedAssetSymbol) || [];
                return filteredTokens.length > 0 ? { ...route, tokens: filteredTokens } : null;
            })
                .filter((r) => r !== null);
        }
        return filteredRoutes;
    }
    return routes;
}
function useRoutes({ direction, values }) {
    const { sourceRoutes, destinationRoutes, networks, extendedRouteFlags } = useSettingsState();
    const apiClient = new LayerSwapApiClient();
    const url = useMemo(() => resolveNetworkRoutesURL(direction, values, undefined, sourceRoutes), [direction, values, sourceRoutes]);
    const defaultRoutes = direction === 'from' ? sourceRoutes : destinationRoutes;
    const { routes, isLoading } = useRoutesData(url, defaultRoutes || [], apiClient.fetcher);
    // Only the opposite side's selection matters per branch; keep the other side's
    // deps inert so selection changes don't needlessly invalidate the memo.
    const fromName = direction === 'to' ? values.from?.name : undefined;
    const toName = direction === 'from' ? values.to?.name : undefined;
    const toSymbol = direction === 'from' ? values.toAsset?.symbol : undefined;
    const finalRoutes = useMemo(() => {
        // Re-add extended sources after SWR revalidation (the backend list lacks them).
        if (direction === 'from')
            return mergeExtendedSourceRoutes(routes, networks, toName, toSymbol, extendedRouteFlags);
        // An extended source is a real backend destination too — exclude it from the
        // destination list when it's the selected source so it can't route to itself.
        return isExtendedSourceNetwork(fromName) ? routes.filter(r => r.name !== fromName) : routes;
    }, [routes, direction, networks, fromName, toName, toSymbol, extendedRouteFlags]);
    return { routes: finalRoutes, isLoading };
}
// ---------- Token Helpers ----------
function resolveTokenUSDBalance(route, token, balances) {
    const networkBalance = balances?.[route.name]?.balances || [];
    const match = networkBalance.find(b => b.token === token.symbol);
    return match?.amount && match.amount > 0 ? match.amount * token.price_in_usd : 0;
}
// ---------- Sorting Functions ----------
const BALANCE_EPSILON = 0.001; // sub-cent threshold for floating-point comparison
function directionKeys(direction) {
    return {
        historyKey: direction === 'from' ? 'sourceRoutes' : 'destinationRoutes',
        rankKey: direction === 'from' ? 'source_rank' : 'destination_rank',
    };
}
function sortRoutesByMostUsed(routes, routesHistory, direction) {
    const { historyKey } = directionKeys(direction);
    const history = routesHistory[historyKey] || {};
    const usageMap = new Map();
    for (const route of routes) {
        usageMap.set(route.name, Object.values(history[route.name] || {}).reduce((sum, count) => sum + count, 0));
    }
    return [...routes].sort((a, b) => {
        const aUsage = usageMap.get(a.name) || 0;
        const bUsage = usageMap.get(b.name) || 0;
        if (bUsage !== aUsage) {
            return bUsage - aUsage;
        }
        return a.display_name.localeCompare(b.display_name);
    });
}
function sortRoutesByTrending(routes, direction) {
    const { rankKey } = directionKeys(direction);
    return [...routes].sort((a, b) => {
        const aRank = a[rankKey] || 999999;
        const bRank = b[rankKey] || 999999;
        if (aRank !== bRank) {
            return aRank - bRank;
        }
        return a.display_name.localeCompare(b.display_name);
    });
}
function sortRoutesAlphabetically(routes, ascending = true) {
    return [...routes].sort((a, b) => {
        const comparison = a.display_name.localeCompare(b.display_name);
        return ascending ? comparison : -comparison;
    });
}
function sortRoutes(routes, sortingOption, direction, balances, routesHistory) {
    switch (sortingOption) {
        case 'relevance':
            return sortRoutesByRelevance(routes, balances, routesHistory, direction);
        case 'most_used':
            return sortRoutesByMostUsed(routes, routesHistory, direction);
        case 'trending':
            return sortRoutesByTrending(routes, direction);
        case 'alphabetical_asc':
            return sortRoutesAlphabetically(routes, true);
        case 'alphabetical_desc':
            return sortRoutesAlphabetically(routes, false);
        default:
            return routes;
    }
}
function sortTokensByMostUsed(tokens, route, routesHistory, direction) {
    const { historyKey } = directionKeys(direction);
    const routeHistory = routesHistory[historyKey]?.[route.name] || {};
    return [...tokens].sort((a, b) => {
        const aUsage = routeHistory[a.symbol] || 0;
        const bUsage = routeHistory[b.symbol] || 0;
        if (bUsage !== aUsage) {
            return bUsage - aUsage;
        }
        return a.symbol.localeCompare(b.symbol);
    });
}
function sortTokensByTrending(tokens, direction) {
    const { rankKey } = directionKeys(direction);
    return [...tokens].sort((a, b) => {
        const aRank = a[rankKey] || 999999;
        const bRank = b[rankKey] || 999999;
        if (aRank !== bRank) {
            return aRank - bRank;
        }
        return a.symbol.localeCompare(b.symbol);
    });
}
function sortTokensAlphabetically(tokens, ascending = true) {
    return [...tokens].sort((a, b) => {
        const comparison = a.symbol.localeCompare(b.symbol);
        return ascending ? comparison : -comparison;
    });
}
function sortTokens(tokens, route, sortingOption, direction, balances, routesHistory) {
    switch (sortingOption) {
        case 'relevance':
            return sortTokensByRelevance(tokens, route, balances, routesHistory, direction);
        case 'most_used':
            return sortTokensByMostUsed(tokens, route, routesHistory, direction);
        case 'trending':
            return sortTokensByTrending(tokens, direction);
        case 'alphabetical_asc':
            return sortTokensAlphabetically(tokens, true);
        case 'alphabetical_desc':
            return sortTokensAlphabetically(tokens, false);
        default:
            return tokens;
    }
}
function sortGroupedTokens(tokenElements, sortingOption, direction, balances, routesHistory) {
    // Sort items within each group
    const groupsWithSortedItems = tokenElements.map(group => {
        const sortedItems = sortGroupedTokenItems(group.items, sortingOption, direction, balances, routesHistory);
        const totalUSD = balances
            ? sortedItems.reduce((sum, item) => sum + resolveTokenUSDBalance(item.route.route, item.route.token, balances), 0)
            : 0;
        return { ...group, items: sortedItems, totalUSD };
    });
    // Sort groups themselves
    switch (sortingOption) {
        case 'relevance':
            return sortGroupedTokensByRelevance(groupsWithSortedItems, balances, routesHistory, direction);
        case 'most_used': {
            const { historyKey } = directionKeys(direction);
            const history = routesHistory[historyKey] || {};
            const historyValues = Object.values(history);
            const symbolUsageMap = new Map();
            for (const g of groupsWithSortedItems) {
                symbolUsageMap.set(g.symbol, historyValues.reduce((sum, routes) => sum + (routes[g.symbol] || 0), 0));
            }
            return groupsWithSortedItems.sort((a, b) => {
                const aUsage = symbolUsageMap.get(a.symbol) || 0;
                const bUsage = symbolUsageMap.get(b.symbol) || 0;
                return bUsage - aUsage || a.symbol.localeCompare(b.symbol);
            });
        }
        case 'trending': {
            const { rankKey } = directionKeys(direction);
            const withMinRank = groupsWithSortedItems.map(g => ({
                ...g,
                minRank: g.items.reduce((min, i) => Math.min(min, i.route.token[rankKey] || 999999), 999999),
            }));
            return withMinRank.sort((a, b) => a.minRank - b.minRank || a.symbol.localeCompare(b.symbol));
        }
        case 'alphabetical_asc':
            return groupsWithSortedItems.sort((a, b) => a.symbol.localeCompare(b.symbol));
        case 'alphabetical_desc':
            return groupsWithSortedItems.sort((a, b) => b.symbol.localeCompare(a.symbol));
        default:
            return groupsWithSortedItems;
    }
}
function sortGroupedTokenItems(items, sortingOption, direction, balances, routesHistory) {
    switch (sortingOption) {
        case 'relevance':
            return sortTokenItemsByRelevance(items, balances, routesHistory, direction);
        case 'most_used': {
            const { historyKey } = directionKeys(direction);
            return [...items].sort((a, b) => {
                const aUsage = routesHistory[historyKey]?.[a.route.route.name]?.[a.route.token.symbol] || 0;
                const bUsage = routesHistory[historyKey]?.[b.route.route.name]?.[b.route.token.symbol] || 0;
                return bUsage - aUsage || a.route.route.display_name.localeCompare(b.route.route.display_name);
            });
        }
        case 'trending': {
            const { rankKey } = directionKeys(direction);
            return [...items].sort((a, b) => {
                const aRank = a.route.token[rankKey] || 999999;
                const bRank = b.route.token[rankKey] || 999999;
                return aRank - bRank || a.route.route.display_name.localeCompare(b.route.route.display_name);
            });
        }
        case 'alphabetical_asc':
            return [...items].sort((a, b) => a.route.route.display_name.localeCompare(b.route.route.display_name));
        case 'alphabetical_desc':
            return [...items].sort((a, b) => b.route.route.display_name.localeCompare(a.route.route.display_name));
        default:
            return items;
    }
}
function sortRoutesByRelevance(routes, balances, routesHistory, direction) {
    const { historyKey, rankKey } = directionKeys(direction);
    const history = routesHistory[historyKey] || {};
    const balanceMap = new Map();
    if (direction === 'from' && balances) {
        for (const route of routes) {
            balanceMap.set(route.name, balances[route.name] ? getTotalBalanceInUSD(balances[route.name], route) || 0 : 0);
        }
    }
    const usageMap = new Map();
    for (const route of routes) {
        usageMap.set(route.name, Object.values(history[route.name] || {}).reduce((sum, count) => sum + count, 0));
    }
    return [...routes].sort((a, b) => {
        if (direction === 'from' && balances) {
            const balanceDiff = (balanceMap.get(b.name) || 0) - (balanceMap.get(a.name) || 0);
            if (Math.abs(balanceDiff) > BALANCE_EPSILON)
                return balanceDiff;
        }
        const aUsage = usageMap.get(a.name) || 0;
        const bUsage = usageMap.get(b.name) || 0;
        if (aUsage !== bUsage)
            return bUsage - aUsage;
        const aRank = a[rankKey] || 999999;
        const bRank = b[rankKey] || 999999;
        if (aRank !== bRank)
            return aRank - bRank;
        return a.display_name.localeCompare(b.display_name);
    });
}
function sortTokensByRelevance(tokens, route, balances, routesHistory, direction) {
    const { historyKey, rankKey } = directionKeys(direction);
    const routeHistory = routesHistory[historyKey]?.[route.name] || {};
    return [...tokens].sort((a, b) => {
        if (direction === 'from' && balances) {
            const aBalance = resolveTokenUSDBalance(route, a, balances);
            const bBalance = resolveTokenUSDBalance(route, b, balances);
            const balanceDiff = bBalance - aBalance;
            if (Math.abs(balanceDiff) > BALANCE_EPSILON)
                return balanceDiff;
        }
        const aUsage = routeHistory[a.symbol] || 0;
        const bUsage = routeHistory[b.symbol] || 0;
        if (aUsage !== bUsage)
            return bUsage - aUsage;
        const aRank = a[rankKey] || 999999;
        const bRank = b[rankKey] || 999999;
        if (aRank !== bRank)
            return aRank - bRank;
        return a.symbol.localeCompare(b.symbol);
    });
}
function sortGroupedTokensByRelevance(groups, _balances, routesHistory, direction) {
    const { historyKey, rankKey } = directionKeys(direction);
    const history = routesHistory[historyKey] || {};
    const withMinRank = [...groups].map(g => ({
        ...g,
        minRank: g.items.reduce((min, i) => Math.min(min, i.route.token[rankKey] || 999999), 999999),
    }));
    const historyValues = Object.values(history);
    const symbolUsageMap = new Map();
    for (const g of withMinRank) {
        symbolUsageMap.set(g.symbol, historyValues.reduce((sum, routes) => sum + (routes[g.symbol] || 0), 0));
    }
    return withMinRank.sort((a, b) => {
        if (direction === 'from') {
            const usdDiff = b.totalUSD - a.totalUSD;
            if (Math.abs(usdDiff) > BALANCE_EPSILON)
                return usdDiff;
        }
        const aUsage = symbolUsageMap.get(a.symbol) || 0;
        const bUsage = symbolUsageMap.get(b.symbol) || 0;
        if (aUsage !== bUsage)
            return bUsage - aUsage;
        if (a.minRank !== b.minRank)
            return a.minRank - b.minRank;
        return a.symbol.localeCompare(b.symbol);
    });
}
function sortTokenItemsByRelevance(items, balances, routesHistory, direction) {
    const { historyKey, rankKey } = directionKeys(direction);
    return [...items].sort((a, b) => {
        if (direction === 'from' && balances) {
            const aBalance = resolveTokenUSDBalance(a.route.route, a.route.token, balances);
            const bBalance = resolveTokenUSDBalance(b.route.route, b.route.token, balances);
            const balanceDiff = bBalance - aBalance;
            if (Math.abs(balanceDiff) > BALANCE_EPSILON)
                return balanceDiff;
        }
        const aUsage = routesHistory[historyKey]?.[a.route.route.name]?.[a.route.token.symbol] || 0;
        const bUsage = routesHistory[historyKey]?.[b.route.route.name]?.[b.route.token.symbol] || 0;
        if (aUsage !== bUsage)
            return bUsage - aUsage;
        const aRank = a.route.token[rankKey] || 999999;
        const bRank = b.route.token[rankKey] || 999999;
        if (aRank !== bRank)
            return aRank - bRank;
        return a.route.route.display_name.localeCompare(b.route.route.display_name);
    });
}
function resolveSearch(routes, search, direction, balances, routesHistory) {
    const matchedNetworks = searchInNetworks(routes, search, direction, balances);
    const matchedTokens = searchInTokens(routes, search).sort(sortSuggestedTokenElements(direction, balances, routesHistory));
    return [
        ...(matchedNetworks.length ? [resolveTitle('Networks'), ...matchedNetworks] : []),
        ...(matchedTokens.length ? [resolveTitle('Tokens'), ...matchedTokens] : [])
    ];
}
const searchInNetworks = (routes, search, direction, balances) => {
    const lower = search.toLowerCase().trim();
    return routes.filter(r => {
        const internalNameMatch = r.name.toLowerCase().includes(lower);
        const displayNameMatch = r.display_name?.toLowerCase().includes(lower);
        return internalNameMatch || displayNameMatch;
    }).map(r => ({
        type: 'network',
        route: {
            ...r,
            tokens: (direction === "from" && balances)
                ? sortNetworkTokens(r, balances)
                : r.tokens
        }
    }));
};
const searchInTokens = (routes, search) => {
    const lower = search.toLowerCase().replace(/\s+/g, " ").trim();
    return extractTokenElementsAsSuggested(routes).filter(e => {
        const { token, route } = e.route;
        const symbolMatch = token.symbol.toLowerCase().includes(lower);
        const contractMatch = token.contract?.toLowerCase().includes(lower);
        const nameMatch = token.display_asset?.toLowerCase().includes(lower);
        const splitted = lower.split(' ');
        const firstpart = splitted?.[0];
        const secondpart = splitted?.[1];
        const combo = (firstpart && secondpart) ? ((token.symbol.toLowerCase().includes(firstpart) && route.name.toLowerCase().includes(secondpart))
            ||
                (token.symbol.toLowerCase().includes(secondpart) && route.name.toLowerCase().includes(firstpart))
            ||
                (token.symbol.toLowerCase().includes(firstpart) && route.display_name.toLowerCase().includes(secondpart))
            ||
                (token.symbol.toLowerCase().includes(secondpart) && route.display_name.toLowerCase().includes(firstpart))) : false;
        return symbolMatch || contractMatch || nameMatch || combo;
    });
};
export function groupRoutes({ routes, direction, balances, groupBy, recents, balancesLoaded, search, suggestionsLimit = 4, sortingOption = SortingOption.RELEVANCE, skipBalanceGate = false, hideSuggestions = false }) {
    if (search) {
        return resolveSearch(routes, search, direction, balances, recents);
    }
    // Suggestions always use relevance-based sorting (unchanged)
    const suggestedRoutes = hideSuggestions
        ? []
        : getSuggestedRoutes(routes, balances, recents, direction, balancesLoaded, suggestionsLimit, skipBalanceGate);
    // Apply custom sorting ONLY to "All Networks/All Tokens" section
    if (groupBy === "token") {
        const groupedTokens = resolveTokenRoutes(routes, balances, direction, recents, sortingOption);
        return mergeGroups(suggestedRoutes, groupedTokens);
    }
    const groupedNetworks = resolveNetworkRoutes(routes, balances, direction, recents, sortingOption);
    return mergeGroups(suggestedRoutes, groupedNetworks);
}
const mergeGroups = (suggestedRoutes, allRoutes) => {
    const allRoutesTitle = allRoutes.find(() => true)?.type === "grouped_token" ? 'All Tokens' : 'All Networks';
    return [
        ...(suggestedRoutes.length ? [resolveTitle('Suggestions'), ...suggestedRoutes] : []),
        resolveTitle(allRoutesTitle),
        ...allRoutes
    ];
};
const resolveNetworkRoutes = (routes, balances, direction, routesHistory, sortingOption = SortingOption.RELEVANCE) => {
    // Sort routes based on selected option
    const sortedRoutes = sortRoutes(routes, sortingOption, direction, balances, routesHistory);
    return sortedRoutes.map(r => ({
        type: 'network',
        route: {
            ...r,
            tokens: sortTokens(r.tokens, r, sortingOption, direction, balances, routesHistory)
        }
    }));
};
const resolveTokenRoutes = (routes, balances, direction, routesHistory, sortingOption = SortingOption.RELEVANCE) => {
    const groupedRoutes = groupByTokens(routes);
    return sortGroupedTokens(groupedRoutes, sortingOption, direction, balances, routesHistory);
};
function filterExchangesByQuery(exchanges, direction, queryParams) {
    const { lockFrom, from, lockTo, to } = queryParams;
    const hasNetworkLock = direction === 'from' ? !!lockFrom : !!lockTo;
    if (!hasNetworkLock)
        return exchanges;
    const lockedExchangeName = direction === 'from'
        ? (lockFrom && from ? normalize(from) : undefined)
        : (lockTo && to ? normalize(to) : undefined);
    if (lockedExchangeName)
        return exchanges.filter(e => normalize(e.name) === lockedExchangeName);
    return exchanges;
}
function groupExchanges(exchangesRoutes, search, direction, queryParams) {
    let exchanges = exchangesRoutes.map((r) => ({ ...r }));
    // Apply query-based filtering if parameters are provided
    if (direction && queryParams) {
        exchanges = filterExchangesByQuery(exchanges, direction, queryParams);
    }
    if (search) {
        exchanges = exchanges.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
    }
    return exchanges.sort((a, b) => a.name.localeCompare(b.name));
}
// ---------- Token Grouping ----------
function groupByTokens(routes) {
    const tokenMap = {};
    for (const r of routes) {
        for (const t of r.tokens || []) {
            const el = { type: 'network_token', route: { token: t, route: r } };
            if (!tokenMap[t.symbol])
                tokenMap[t.symbol] = [];
            tokenMap[t.symbol].push(el);
        }
    }
    const result = Object.entries(tokenMap)
        .map(([symbol, items]) => ({
        type: 'grouped_token',
        symbol,
        items
    }));
    return result;
}
// ---------- Sorting ----------
function sortNetworkTokens(route, balances) {
    return [...(route.tokens || [])].sort((a, b) => {
        const balanceA = resolveTokenUSDBalance(route, a, balances || {});
        const balanceB = resolveTokenUSDBalance(route, b, balances || {});
        if (balanceB !== balanceA) {
            return balanceB - balanceA; // Descending by balance
        }
        return a.symbol.localeCompare(b.symbol); // Ascending by symbol
    });
}
// ---------- Resolvers ----------
function resolveSelectedRoute(values, direction) {
    return direction === 'from' ? values.from : values.to;
}
function resolveSelectedToken(values, direction) {
    return direction === 'from' ? values.fromAsset : values.toAsset;
}
// ---------- Exchange ----------
function useExchangeRoutes({ values }) {
    const { sourceExchanges, sourceRoutes } = useSettingsState();
    const apiClient = new LayerSwapApiClient();
    const exchangeRoutesURL = useMemo(() => resolveExchangesURLForSelectedToken(values, sourceRoutes), [values, sourceRoutes]);
    const { data: apiResponse, isLoading, } = useSWR(exchangeRoutesURL, apiClient.fetcher, { keepPreviousData: true, dedupingInterval: 10000 });
    const defaultData = sourceExchanges || [];
    const [exchangesRoutes, setExchangesData] = useState(defaultData);
    useEffect(() => {
        if (!isLoading && apiResponse?.data)
            setExchangesData(apiResponse.data);
    }, [apiResponse]);
    const res = exchangesRoutes.map(r => ({ ...r, cex: true }));
    return { exchangesRoutes: res, isLoading };
}
function getSuggestedRoutes(routes, balances, routesHistory, direction, balancesLoading, limit = 4, skipBalanceGate = false) {
    // Ensure minimum of 4 suggestions
    const effectiveLimit = Math.max(4, limit);
    if (direction === "from" && !skipBalanceGate) {
        if (!balancesLoading && !balances)
            return [];
        if (balancesLoading && direction === "from")
            return Array(effectiveLimit).fill({ type: "sceleton_token" });
    }
    const tokenElements = extractTokenElementsAsSuggested(routes).filter(t => t.route.token.status === "active");
    const sorted = tokenElements.sort(sortSuggestedTokenElements(direction, balances, routesHistory));
    return sorted.slice(0, effectiveLimit);
}
const resolveTitle = (text) => {
    return { type: 'group_title', text };
};
const normalize = (v) => (v ?? "").toLowerCase();
